<template>
  <el-card class="container" v-loading="loading" shadow="never">
    <div slot="header" class="detail-header">
      <div>
        <el-button class="backBtn" type="text" icon="el-icon-arrow-left" @click="close">返回商城订单</el-button>
        <span class="font18">订单详情</span>
      </div>
      <span class="status-badge" :class="`status-${statusAlias(detail.orderStatus)}`">{{ statusText(detail.orderStatus) }}</span>
    </div>

    <el-empty v-if="!loading && error" :description="error">
      <el-button type="primary" @click="getOrderInfo(detail.orderId)">重新加载</el-button>
    </el-empty>

    <div v-else class="detail-content">
      <div class="detail-left">
        <el-card class="info-card" shadow="never">
          <div slot="header">
            <span>订单信息</span>
          </div>
          <div class="base-grid">
            <div class="info-box">
              <div class="info-box-title">订单号</div>
              <div class="info-box-content">{{ detail.orderNo || '-' }}</div>
            </div>
            <div class="info-box">
              <div class="info-box-title">支付方式</div>
              <div class="info-box-content">{{ detail.payTypeText || '-' }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="info-card" shadow="never">
          <div slot="header">
            <span>收货信息</span>
          </div>
          <div class="base-grid">
            <div class="info-box">
              <div class="info-box-title">收货人</div>
              <div class="info-box-content">{{ detail.receiver ? detail.receiver.receiverName : '-' }}</div>
            </div>
            <div class="info-box">
              <div class="info-box-title">联系电话</div>
              <div class="info-box-content">{{ detail.receiver ? detail.receiver.receiverPhone : '-' }}</div>
            </div>
            <div class="info-box wide">
              <div class="info-box-title">收货地址</div>
              <div class="info-box-content">{{ fullAddress }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="info-card" shadow="never">
          <div slot="header">
            <span>商品信息</span>
          </div>
          <div class="table-wrap">
            <el-table :data="detail.items" :header-cell-style="{background:'#F5F5F5'}" border>
              <el-table-column prop="goodsName" label="商品名称" min-width="220" show-overflow-tooltip></el-table-column>
              <el-table-column prop="skuNo" label="货号" width="140" show-overflow-tooltip></el-table-column>
              <el-table-column prop="goodsSpec" label="规格" width="140" show-overflow-tooltip></el-table-column>
              <el-table-column label="单价" width="110" align="center">
                <template slot-scope="scope">￥{{ Number(scope.row.goodsPrice || scope.row.unitPrice || 0).toFixed(2) }}</template>
              </el-table-column>
              <el-table-column prop="goodsNum" label="数量" width="80" align="center"></el-table-column>
              <el-table-column label="小计" width="110" align="center">
                <template slot-scope="scope">￥{{ Number(scope.row.goodsAmount || scope.row.lineAmount || 0).toFixed(2) }}</template>
              </el-table-column>
            </el-table>
          </div>
        </el-card>

        <el-card class="info-card" shadow="never" v-if="detail.invoiceApplication">
          <div slot="header">
            <span>开票申请</span>
          </div>
          <div class="base-grid">
            <div class="info-box">
              <div class="info-box-title">申请单号</div>
              <div class="info-box-content">{{ detail.invoiceApplication.invoiceNo || '-' }}</div>
            </div>
            <div class="info-box">
              <div class="info-box-title">申请状态</div>
              <div class="info-box-content">{{ issueStatusText(detail.invoiceApplication.issueStatus) }}</div>
            </div>
            <div class="info-box">
              <div class="info-box-title">发票抬头</div>
              <div class="info-box-content">{{ detail.invoiceApplication.invoiceTitle || '-' }}</div>
            </div>
            <div class="info-box">
              <div class="info-box-title">开票金额</div>
              <div class="info-box-content redColor">￥{{ Number(detail.invoiceApplication.invoiceAmount || 0).toFixed(2) }}</div>
            </div>
          </div>
        </el-card>
      </div>

      <div class="detail-right">
        <el-card class="info-card" shadow="never">
          <div slot="header">
            <span>费用明细</span>
          </div>
          <div class="amount-box">
            <div><span>商品总额</span><b>￥{{ detail.amount ? detail.amount.goodsAmount : '0.00' }}</b></div>
            <div><span>运费</span><b>￥{{ detail.amount ? detail.amount.freightAmount : '0.00' }}</b></div>
            <div><span>品牌折扣</span><b>-￥{{ detail.amount ? detail.amount.brandDiscountAmount : '0.00' }}</b></div>
            <div class="final"><span>应付金额</span><strong>￥{{ detail.amount ? detail.amount.totalAmount : '0.00' }}</strong></div>
          </div>
          <div class="detail-actions">
            <el-button v-if="!isTeamOrder && actionAvailable('pay')" type="primary" @click="emitPay">立即支付</el-button>
            <el-button v-if="!isTeamOrder && actionAvailable('cancel')" @click="emitCancel">取消订单</el-button>
            <el-button v-if="!isTeamOrder && actionAvailable('confirm')" type="primary" @click="emitConfirm">确认收货</el-button>
            <el-button v-if="actionAvailable('invoice')" @click="emitInvoice">申请开票</el-button>
          </div>
        </el-card>

        <el-card class="info-card" shadow="never">
          <div slot="header">
            <span>订单动态</span>
          </div>
          <div class="timeline">
            <template v-if="detail.operationLogs && detail.operationLogs.length">
              <div v-for="(event, index) in detail.operationLogs" :key="event.logId || index" class="timeline-item done">
                <i class="dot"></i>
                <div>
                  <p>{{ event.operation || event.actionName || event.actionType || '订单操作' }}</p>
                  <span>{{ event.time || event.addDate || '-' }}</span>
                  <small v-if="event.operator || event.operationRemark">{{ event.operator || event.operationRemark }}</small>
                </div>
              </div>
            </template>
            <template v-else-if="detail.timeline && detail.timeline.length">
              <div v-for="(event, index) in detail.timeline" :key="index" class="timeline-item done">
                <i class="dot"></i>
                <div>
                  <p>{{ event.event || '-' }}</p>
                  <span>{{ event.time || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </el-card>
      </div>
    </div>
  </el-card>
</template>

<script>
import { mallOrderInfo } from "@/api/request";

export default {
  props: {
    clientId: {
      type: [String, Number],
      default: ''
    },
    orderType: {
      type: String,
      default: 'personal',
      validator: value => ['personal', 'team'].includes(value)
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      detail: { orderId: '' }
    }
  },
  computed: {
    isTeamOrder() {
      return this.orderType === 'team'
    },
    fullAddress() {
      const receiver = this.detail.receiver || {}
      const province = receiver.province || this.detail.province || ''
      const city = receiver.city || this.detail.city || ''
      const district = receiver.district || this.detail.district || ''
      const address = receiver.receiverAddress || receiver.detailAddress || this.detail.receiverAddress || this.detail.detailAddress || ''
      return [province, city, district].join('') + (address ? ' ' + address : '') || '-'
    },
    
  },
  methods: {
    statusAlias(status) {
      const map = {
        '0': 'pending',
        '1': 'processing',
        '2': 'shipped',
        '3': 'delivered',
        '4': 'cancelled',
        '5': 'audit',
        '6': 'auditRejected'
      }
      return map[String(status)] || 'processing'
    },
    statusText(status) {
      const map = {
        '0': '待支付',
        '1': '待发货',
        '2': '待收货',
        '3': '已完成',
        '4': '已取消',
        '5': '待审核',
        '6': '审核拒绝'
      }
      return map[String(status)] || '处理中'
    },
    invoiceStatusText(status) {
      const map = {
        '1': '未开票',
        '2': '已开票',
        '3': '无需开票'
      }
      return map[String(status)] || '-'
    },
    issueStatusText(issueStatus) {
      const map = {
        '1': '开票中',
        '2': '已开票',
      }
      return map[String(issueStatus)] || '未开票'
    },
    actionAvailable(action) {
      const status = String(this.detail.orderStatus)
      const key = action === 'pay' ? 'canPay' : action === 'cancel' ? 'canCancel' : action === 'confirm' ? 'canConfirm' : 'canApplyInvoice'
      if (Object.prototype.hasOwnProperty.call(this.detail, key)) {
        return this.detail[key] === true || this.detail[key] === 'true'
      }
      if (action === 'pay') return status === '0'
      if (action === 'cancel') return status === '0' || status === '1'
      if (action === 'confirm') return status === '2'
      return status === '3' && String(this.detail.invoiceStatus) === '1'
    },
    async getOrderInfo(orderId) {
      if (!orderId || !this.clientId) return
      this.loading = true
      this.error = ''
      this.detail = { orderId }
      try {
        const res = await mallOrderInfo({ orderId, clientId: this.clientId })
        const data = res && res.data
        if (!data) {
          this.error = '订单不存在或暂无详情数据'
          return
        }
        this.detail = data
      } catch (error) {
        this.error = (error && error.message) || '订单详情加载失败'
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$emit('close')
    },
    emitPay() {
      this.$emit('pay', this.detail)
    },
    emitCancel() {
      this.$emit('cancel', this.detail)
    },
    emitConfirm() {
      this.$emit('confirm', this.detail)
    },
    emitInvoice() {
      this.$emit('invoice', this.detail)
    }
  }
}
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  min-height: 700px;
  box-sizing: border-box;
  background-color: #fff;
  overflow: visible;
}

.container ::v-deep .el-card__body {
  overflow: visible;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 20px;
}

.detail-left,
.detail-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  border: 1px solid #e8e8e8;
}

.base-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.info-box {
  flex: 1;
  min-width: 260px;
  display: flex;
  border: 1px solid #ddd;
  &.wide {
    flex-basis: 100%;
  }
  .info-box-title {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 110px;
    background-color: #84B7F950;
  }
  .info-box-content {
    display: flex;
    align-items: center;
    padding: 14px;
    width: calc(100% - 110px);
    min-width: 0;
    word-break: break-all;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 10px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
}

.status-pending { background: #fff3e7; color: #c2410c; }
.status-audit { background: #fff8db; color: #a16207; }
.status-processing { background: #eaf1ff; color: #315fdc; }
.status-shipped { background: #f0eaff; color: #6d28d9; }
.status-delivered { background: #e7f8eb; color: #168239; }
.status-cancelled { background: #eef0f3; color: #666; }
.status-auditRejected { background: #fff0f0; color: #dc2626; }

.amount-box {
  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    color: #666;
  }
  b {
    color: #111;
  }
  .final {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e8e8e8;
    strong {
      color: #FF4A2B;
      font-size: 28px;
      line-height: 1;
    }
  }
}

.detail-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .el-button {
    width: 100%;
    margin-left: 0;
  }
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.timeline-item {
  display: flex;
  gap: 10px;
  opacity: 0.45;
  &.done {
    opacity: 1;
  }
  p {
    margin: 0;
    font-weight: 600;
    color: #111;
  }
  span,
  small {
    display: block;
    margin-top: 4px;
    color: #999;
    font-size: 12px;
  }
}

.dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5CC300;
  margin-top: 6px;
}

.table-wrap {
  overflow-x: auto;
}

@media (max-width: 1200px) {
  .detail-content {
    grid-template-columns: 1fr;
  }
}
</style>
